import type { SelectItemProps } from '@ariakit/react';
import {
  ComboboxItem,
  ComboboxList,
  VisuallyHidden,
  useComboboxStore,
  useSelectStore,
} from '@ariakit/react';
import { debounce, isArray } from 'lodash';
import { matchSorter } from 'match-sorter';
import type { ChangeEvent, ComponentProps, KeyboardEvent } from 'react';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { getTooltipText } from '../../utils/get-tooltip-text';
import { TypographyVariant } from '../../utils/theme';
import Icon, { IconName } from '../icon/icon';
import InputFeedback from '../input-feedback';
import InputLabelWithHint from '../input-label/input-label-with-hint';
import { Tooltip } from '../tooltip/tooltip';
import {
  CreateButton,
  CreationErrorWrapper,
  HiddenCombobox,
  NoResultsFound,
  SearchIconSpan,
  SelectOption,
  SelectWrapper,
  StyledAriaSelect,
  StyledCombobox,
  StyledMessageTypography,
  StyledSelectPopover,
} from './components/select.style';
import Selected from './components/selected';

const AriaSelectRoot = forwardRef(
  (props: ComponentProps<typeof StyledAriaSelect>, selectRef) => (
    <StyledAriaSelect {...props} ref={selectRef as never} />
  )
);

AriaSelectRoot.displayName = 'AriaSelectRoot';

export interface ISelectOption {
  label: string;
  key: string;
}

export interface ISelect extends Omit<SelectItemProps, 'onChange' | 'value'> {
  options: ISelectOption[];
  name: string;
  showLabel?: boolean;
  placeholder?: string;
  placeholderColor?: string;
  selectedVariant?: TypographyVariant;
  showSearch?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  warningMessage?: string;
  successMessage?: string;
  searchPlaceholder?: string;
  onSearchChange?: (value: string) => void;
  width?: string;
  onChange: (value: string | string[]) => void;
  defaultValue?: string | string[];
  value?: string | string[];
  clearable?: boolean;
  hasRightDivider?: boolean;
  onCreateOption?: (label: string) => void;
  canCreateOption?: boolean;
  createText?: string;
  hasCreationError?: boolean;
  hintText?: string;
  noResultsFoundText?: string;
  disabledReasonText?: string;
  optionsTooltipMaxCharCount?: number;
  required?: boolean;
  /** Set to true to only call `onChange` and always show placeholder */
  menuList?: boolean;
  closeButtonLabel?: string;
  id?: string;
  popoverMaxHeight?: string;
}

const Select = forwardRef(
  (
    {
      options,
      name,
      value,
      showLabel = true,
      placeholder,
      placeholderColor,
      selectedVariant,
      showSearch = false,
      hasError = false,
      errorMessage = '',
      warningMessage,
      successMessage,
      searchPlaceholder = 'Search',
      onSearchChange,
      width,
      onChange,
      defaultValue,
      clearable = false,
      hasRightDivider = false,
      onCreateOption,
      canCreateOption = false,
      createText = 'Create',
      hasCreationError = false,
      hintText,
      noResultsFoundText = 'No results found',
      optionsTooltipMaxCharCount = 100,
      disabledReasonText,
      required,
      menuList = false,
      closeButtonLabel,
      id,
      popoverMaxHeight,
      onBlur,
      ...rest
    }: ISelect,
    ref
  ) => {
    const [selectValue, setSelectValue] = useState(value ?? defaultValue ?? '');
    const combobox = useComboboxStore({
      virtualFocus: false,
      resetValueOnHide: true,
      focusLoop: true,
    });

    const theme = useTheme();

    const uniqueId = useId();

    const selectId = id || uniqueId;

    const { t } = useTranslation();

    const ariaDescribedBy = useMemo(() => {
      if (hasError) return `${selectId}-error`;
      return hintText ? `${getTooltipText(name)}` : undefined;
    }, [hasError, hintText, name, selectId]);

    const ariaLabel = useMemo(() => {
      if (isArray(selectValue) || !selectValue) {
        return name;
      }
      const selectedOption = options?.find(
        (option) => option.key === selectValue
      );
      return `${selectedOption?.label}, ${name}`;
    }, [selectValue, options, name]);

    useEffect(() => {
      value && setSelectValue(value);
    }, [value]);

    const select = useSelectStore({
      combobox,
      value: selectValue,
      setValue: (value: string) => {
        if (!menuList) {
          setSelectValue(value ?? '');
        }
        onChange(value ?? '');
      },
    });

    const handleRemoveSelectValue = (value: string) => {
      if (!Array.isArray(selectValue)) return;
      const newValue = selectValue.filter((item) => item !== value);
      setSelectValue(newValue);
      onChange(newValue);
    };

    const handleClearSelectValue = () => {
      // Keep the type of the value so a multi-select cannot be cleared into a
      // string, which would break both the schema and this component's own
      // single vs multi handling
      const clearedValue = Array.isArray(selectValue) ? [] : '';
      setSelectValue(clearedValue);
      onChange(clearedValue);
    };
    const optionsMap = options?.reduce(
      (object, option) => {
        object[option.key] = option.label;
        return object;
      },
      {} as Record<string, string>
    );
    const comboBoxValue = combobox.useState('value');

    const matches = useMemo(() => {
      if (!comboBoxValue) {
        return options;
      }
      return matchSorter(options, comboBoxValue, {
        keys: ['label'],
      });
    }, [comboBoxValue, options]);

    const isTop = select.useState('currentPlacement').includes('top');

    const isDirty =
      typeof selectValue === 'string'
        ? Boolean(selectValue && selectValue !== '')
        : selectValue.length > 0;
    const isOpen = combobox.useState('open');

    const isCreateButtonVisible = useMemo(() => {
      return canCreateOption && comboBoxValue !== '';
    }, [canCreateOption, comboBoxValue]);

    const handleOnCreateOption = useCallback(() => {
      if (onCreateOption && comboBoxValue) {
        onCreateOption(comboBoxValue);
      }
    }, [onCreateOption, comboBoxValue]);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        switch (event.key) {
          case 'ArrowDown':
          case 'ArrowUp': {
            const activeId = combobox.getState().activeId;
            combobox.move(activeId);
            break;
          }

          case 'Enter':
          case ' ': {
            const elements = event.target as HTMLElement;
            const element = elements.querySelector(
              '[data-testid]'
            ) as HTMLElement;
            const currentElement = element?.dataset['testid'];
            if (currentElement) {
              if (typeof selectValue === 'string') {
                setSelectValue(currentElement);
                onChange(currentElement);
                select.toggle();
              } else {
                if (selectValue.includes(currentElement)) {
                  setSelectValue(
                    selectValue.filter((item) => item !== currentElement)
                  );
                  onChange(
                    selectValue.filter((item) => item !== currentElement)
                  );
                } else {
                  setSelectValue([...selectValue, currentElement]);
                  onChange([...selectValue, currentElement]);
                }
              }
            }
            event.preventDefault();
            event.stopPropagation();
            break;
          }
          case 'Escape': {
            combobox.toggle();
            select.toggle();
            break;
          }
        }
      },
      [combobox, onChange, select, selectValue]
    );

    // Memoised so the timer survives re-renders, otherwise every render hands
    // the input a fresh debounce and the search fires on each keystroke
    const debouncedSearchChange = useMemo(
      function createDebouncedSearchChange() {
        return debounce((event: ChangeEvent<HTMLInputElement>) => {
          onSearchChange?.(event.target.value);
        }, 500);
      },
      [onSearchChange]
    );

    return (
      <SelectWrapper
        width={width}
        data-testid="select-component-wrapper"
        className="select-component-wrapper"
      >
        <InputLabelWithHint
          inputId={selectId}
          showLabel={showLabel}
          hintText={hintText}
          hasError={hasError}
          name={name}
        />
        <AriaSelectRoot
          ref={ref}
          data-testid="select-component"
          className="fs-mask"
          required={required}
          {...rest}
          id={selectId}
          store={select}
          $isDirty={isDirty}
          $hasError={hasError}
          $isTop={isTop}
          width={width}
          $hasRightDivider={hasRightDivider}
          // eslint-disable-next-line jsx-a11y/aria-role
          role={undefined} // role is set to combobox by default but we want to override it
          aria-autocomplete={undefined}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-invalid={hasError}
        >
          <Selected
            selectValue={selectValue}
            optionsMap={optionsMap}
            placeholder={placeholder}
            placeholderColor={placeholderColor || theme.palette.neutral[100]}
            selectedVariant={selectedVariant}
            handleRemoveSelectValue={handleRemoveSelectValue}
            open={isOpen}
            onClear={handleClearSelectValue}
            clearable={clearable}
            disabled={Boolean(rest.disabled)}
            optionsTooltipMaxCharCount={optionsTooltipMaxCharCount}
            hasError={hasError}
            closeButtonLabel={closeButtonLabel}
          />
        </AriaSelectRoot>
        {disabledReasonText && Boolean(rest?.disabled) && (
          <StyledMessageTypography
            variant={TypographyVariant.bodySm1}
            color={theme.palette.neutral[90]}
            data-testid="select-disabled-text"
          >
            {disabledReasonText}
          </StyledMessageTypography>
        )}
        <StyledSelectPopover
          sameWidth
          store={select}
          $showSearch={showSearch}
          width={width}
          maxHeight={popoverMaxHeight}
          $isTop={isTop}
          data-testid="select-popover"
          $hasError={hasError}
          role="none"
          onBlur={onBlur}
        >
          <VisuallyHidden aria-live="polite">
            {t('phrases.useArrowKeysToNavigate')}
          </VisuallyHidden>
          {showSearch ? (
            <>
              <StyledCombobox
                store={combobox}
                autoSelect
                placeholder={searchPlaceholder}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                data-testid="select-search-text-field"
                aria-label={searchPlaceholder}
                onChange={onSearchChange && debouncedSearchChange}
                $hasError={hasCreationError}
                $canCreateOption={canCreateOption}
              />
              <VisuallyHidden aria-live="polite">
                {t('phrases.selectSearchResults', { count: matches.length })}
              </VisuallyHidden>
              <SearchIconSpan $isCreateButtonVisible={isCreateButtonVisible}>
                <Icon
                  name={IconName.Search}
                  color={
                    hasCreationError
                      ? theme.palette.error.main
                      : theme.palette.primary.main
                  }
                />
              </SearchIconSpan>
              {isCreateButtonVisible && (
                <CreateButton
                  id="select-create-button"
                  label={createText}
                  onClick={handleOnCreateOption}
                ></CreateButton>
              )}

              {hasCreationError && errorMessage && (
                <CreationErrorWrapper>
                  <InputFeedback
                    errorMessage={errorMessage}
                    data-testid="select-error"
                    id={`${selectId}-error`}
                  />
                </CreationErrorWrapper>
              )}
            </>
          ) : (
            <VisuallyHidden aria-live="polite">
              {t('phrases.selectSearchResults', { count: matches.length })}
            </VisuallyHidden>
          )}
          {!showSearch && (
            // Show a hidden combobox when search is disabled to
            // retain combobox functionality eg keyboard inputs
            <HiddenCombobox store={combobox} />
          )}
          <ComboboxList
            store={combobox}
            onKeyDown={handleKeyDown}
            aria-label={`${name} options`}
          >
            {matches.length > 0 || !showSearch ? (
              matches?.map((option: ISelectOption) => (
                <ComboboxItem
                  key={option.key}
                  render={(props) => {
                    return option.label.length > optionsTooltipMaxCharCount ? (
                      <Tooltip tooltipText={option.label}>
                        <SelectOption
                          {...props}
                          value={option.key}
                          data-testid={option.key}
                          width={width}
                          className="select-option"
                          aria-selected={selectValue === option.key}
                        >
                          {option.label}
                        </SelectOption>
                      </Tooltip>
                    ) : (
                      <SelectOption
                        {...props}
                        value={option.key}
                        data-testid={option.key}
                        width={width}
                        className="select-option"
                        aria-selected={selectValue === option.key}
                      >
                        {option.label}
                      </SelectOption>
                    );
                  }}
                />
              ))
            ) : (
              <NoResultsFound width={width}>
                {noResultsFoundText}
              </NoResultsFound>
            )}
          </ComboboxList>
        </StyledSelectPopover>
        <InputFeedback
          errorMessage={hasError ? errorMessage : undefined}
          warningMessage={warningMessage}
          successMessage={successMessage}
          data-testid="select-error"
          id={`${selectId}-error`}
        />
      </SelectWrapper>
    );
  }
);

Select.displayName = 'DeprecatedSelect';

/**
 * @deprecated
 * Please use the new select component from `@ben/orion/select`
 * */
export default memo(Select);
